/**
 * One wire item's allergen and dietary disclosure as CHIPS (ORDR-165), for the
 * app's own Menu and Item screens. The vocabulary is `disclosure.ts`'s, so the
 * chip and the sentence can never name an allergen two ways.
 *
 * Allergens come first, one chip per state, in commerce's `AllergenState`
 * order: "Contains: wheat, soy", then "May contain: sesame". MAY_CONTAIN is a
 * declared RISK and is never folded into either neighbour. Then one chip per
 * dietary tag.
 *
 * WHAT DRAWS NOTHING. `allergens: null` (nothing recorded) and an explicit
 * ABSENT. A chip is a short label with no room for "only this one was
 * declared", so a chip may never read as "free from"; the standing direction
 * above the menu (`MenuDisclosure`) says the rest. A dietary `[]` is no claim.
 *
 * WHAT IS SHOWN, NOT DROPPED. An unknown allergen, state or tag by its code,
 * and a malformed key as "information unavailable": dropping either could
 * hide a CONTAINS.
 */

import { ALLERGENS, DIETARY } from "./disclosure.js";
import { readWireDisclosure } from "./wire.js";

const STATE_LEADS: Readonly<Record<string, string>> = {
  CONTAINS: "Contains",
  MAY_CONTAIN: "May contain",
};

/** States that draw no chip. */
const SILENT_STATES = new Set(["ABSENT"]);

function capitalised(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** The chips for one wire menu item, allergens first. Never throws. */
export function chipsFor(raw: unknown): string[] {
  const read = readWireDisclosure(raw);
  const chips: string[] = [];

  if (read.allergens.kind === "malformed") {
    chips.push("Allergen information unavailable");
  } else if (read.allergens.kind === "recorded") {
    // Known states in their fixed order, then any unknown state as sent.
    const states = [
      ...Object.keys(STATE_LEADS),
      ...read.allergens.value.map((entry) => entry.state).filter((state) => !(state in STATE_LEADS)),
    ];
    for (const state of new Set(states)) {
      if (SILENT_STATES.has(state)) {
        continue;
      }
      const named = read.allergens.value
        .filter((entry) => entry.state === state)
        .map((entry) => ALLERGENS[entry.allergen] ?? entry.allergen);
      if (named.length > 0) {
        chips.push(`${STATE_LEADS[state] ?? state}: ${named.join(", ")}`);
      }
    }
  }

  if (read.dietaryTags.kind === "recorded") {
    chips.push(...read.dietaryTags.value.map((tag) => (tag in DIETARY ? capitalised(DIETARY[tag] ?? tag) : tag)));
  } else {
    chips.push("Dietary information unavailable");
  }
  return chips;
}
